/** State that is returned when something is registered to the async data controller. */
export interface AsyncDataState<T> {
  data?: T;
  error?: unknown;
  isLoading: boolean;
  hasLoaded: boolean;
}
/** Props for registering something to the async data controller */
export interface AsyncDataProps<T> {
  /**
   * The id used to store the related callbacks in the Map/Cache. Make sure this is shared only
   * among things that are listening to the same promise, otherwise things will need their own
   * unique dataId.
   */
  dataId: string;
  /** The time to live in the cache. */
  ttlMs?: number;
  /**
   * Async function that we want to track with the async data controller. Will not fire if entry is
   * found in the cache.
   */
  dataAsync: () => Promise<T>;
  /** Fires if the async function errors (or onData errors). */
  onError?: (error: unknown) => void;
  /** Fires when the async function resolves. */
  onData?: (data: T) => void;
}

export interface CacheEntry {
  data?: unknown;
  error?: unknown;
  expiryTime: number;
}

type DataListener<T = unknown> = (data: T) => void;
type ErrorListener = (error: unknown) => void;

/**
 * Class that helps track the completion of multiple async functions. This can be used to make sure
 * all async calls have finished running on the server side before passing the html to the client.
 *
 * Also includes a simple cache to prevent re-fetching data that we already have.
 */
export class AsyncDataController {
  private readonly clock: () => number;
  private readonly resultCache: Map<string, CacheEntry>;
  private readonly onDataListeners = new Map<string, Set<DataListener>>();
  private readonly onErrorListeners = new Map<string, Set<ErrorListener>>();
  private readonly unsettledPromises = new Map<string, Promise<unknown>>();
  private readonly defaultExpiryTimeMs: number;
  private readonly errorHandler: (e: unknown) => unknown;
  private settledPromiseCount = 0;

  constructor(props: {
    clock?: () => number;
    cache?: Map<string, CacheEntry>;
    defaultExpiryTimeMs?: number;
    errorHandler?: (e: unknown) => unknown;
  }) {
    this.clock = props.clock ?? Date.now;
    this.resultCache = props.cache ?? new Map<string, CacheEntry>();
    this.defaultExpiryTimeMs = props.defaultExpiryTimeMs ?? 30e3;
    this.errorHandler = props.errorHandler ?? console.error;
  }

  /**
   * Registers an async function to the AsyncDataController. This is used to tell the controller
   * that this is an async call that we want to wait for. If the dataId is already found in the
   * cache, it will return the data, otherwise it will return the loading state.
   *
   * TODO: https://jira.sc-corp.net/browse/ENTWEB-8313 Add Retry Capabilities !IMPORTANT!
   *
   * @param props List of props and options for registering an async function to the listener
   * @returns The state of the function. If it is loading, it will return the loading state. If the
   *   promise has resolved already, it will return the data found in the cache.
   */
  public register = <T>(props: AsyncDataProps<T>): AsyncDataState<T> => {
    const now = this.clock();

    // Check to see if data is in the store.
    if (this.resultCache.has(props.dataId)) {
      const cachedEntry = this.resultCache.get(props.dataId)!;

      if (cachedEntry.expiryTime >= now) {
        if (cachedEntry.error) {
          return {
            error: cachedEntry.error,
            hasLoaded: true,
            isLoading: false,
          };
        }
        // Not checking for !!data because it's OK for it to be undefined.
        return {
          data: cachedEntry.data as T,
          hasLoaded: true,
          isLoading: false,
        };
      }
      this.delete(props.dataId);
    }

    // Adds listeners for future events.
    if (!this.onDataListeners.has(props.dataId)) this.onDataListeners.set(props.dataId, new Set());
    if (!this.onErrorListeners.has(props.dataId))
      this.onErrorListeners.set(props.dataId, new Set());

    props.onData &&
      (this.onDataListeners.get(props.dataId)! as Set<DataListener<T>>).add(props.onData);
    props.onError && this.onErrorListeners.get(props.dataId)!.add(props.onError);

    // If promise is already in progress, return is loading state.
    if (this.unsettledPromises.has(props.dataId)) {
      return {
        hasLoaded: false,
        isLoading: true,
      };
    }

    const promise = props.dataAsync();
    this.unsettledPromises.set(props.dataId, promise);
    promise
      .then(result => {
        this.resultCache.set(props.dataId, {
          data: result,
          expiryTime: this.clock() + (props.ttlMs ?? this.defaultExpiryTimeMs),
        });

        const dataListeners = this.onDataListeners.get(props.dataId)!;

        for (const listener of dataListeners) {
          try {
            listener(result);
          } catch (e) {
            try {
              this.errorHandler(e);
            } catch (_) {
              /* Explicitly do nothing. */
            }
          }
        }
        dataListeners.clear();
      })
      .catch(error => {
        this.resultCache.set(props.dataId, {
          error,
          expiryTime: this.clock() + (props.ttlMs ?? this.defaultExpiryTimeMs),
        });

        const errorListeners = this.onErrorListeners.get(props.dataId)!;

        for (const listener of errorListeners) {
          try {
            listener(error);
          } catch (e) {
            try {
              this.errorHandler(e);
            } catch (_) {
              /* Explicitly do nothing. */
            }
          }
        }

        errorListeners.clear();
      })
      .finally(() => {
        this.unsettledPromises.delete(props.dataId);
        this.settledPromiseCount++;
      });

    return {
      isLoading: true,
      hasLoaded: false,
    };
  };

  /** Returns the number of unsettledPromises */
  public countUnsettledPromises = (): number => {
    return this.unsettledPromises.size;
  };

  /**
   * Returns the number of promises that have settled.
   *
   * This can be useful to know if there were async changes that happened and completed which isn't
   * captured by `countUnsettledPromises`.
   */
  public countSettledPromises = (): number => {
    return this.settledPromiseCount;
  };

  /** Returns the keys (dataId) of unsettledPromises */
  public unawaitedPromiseKeys = (): string[] => {
    return [...this.unsettledPromises.keys()];
  };

  /**
   * Function that waits for all promises to finish. We do not return results as of right now,
   * because there is no need to handle them at this level (since we attach onData or onError). We
   * might need to return whether or not there were any rejections though. TBD.
   *
   * TODO: revisit how we handle errors at this level
   *
   * @returns A Promise that returns nothing
   */
  public allUnawaitedPromises = async (): Promise<void> => {
    await Promise.allSettled(this.unsettledPromises.values());
  };

  /**
   * Removes expired entries in cache and returns the cache.
   *
   * @returns Cached data.
   */
  public getCache = (): Map<string, CacheEntry> => {
    this.cleanCache();
    return this.resultCache;
  };

  /**
   * Removes expired entries from the cache, and returns how many entries were removed.
   *
   * @returns Number of entries removed from the cache.
   */
  public cleanCache = (): number => {
    let numDeleted = 0;

    this.resultCache.forEach((value, key) => {
      if (value.expiryTime < this.clock()) {
        this.delete(key);
        numDeleted++;
      }
    });

    return numDeleted;
  };

  /**
   * Deletes all listeners and the entry in the result cache if it exists.
   *
   * @param key DataId of the entry you want to clear
   */
  public delete = (key: string): void => {
    this.resultCache.delete(key);
    this.onDataListeners.delete(key);
    this.onErrorListeners.delete(key);
  };
}

export const asyncDataController = new AsyncDataController({ clock: Date.now });
